import { ArrowDown, ArrowUp, Plus, Trash2 } from "lucide-react";

import type { PlanConfirmationPayload } from "@/api/grant_opportunity_plan_confirmations";
import type { PlanProposal } from "@/api/grant_opportunity_plan_proposals";
import type { MunicipalityCollaborator } from "@/api/municipality_collaborators";
import {
  MilestoneOwnerSelect,
  ownerSelectOptions,
} from "@/components/grant_suite/MilestoneOwnerSelect";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

// The review step of the planning modal (ENG-1822): the drafted proposal
// treated like a diff: every milestone, task, required document, and
// requirement can be edited, reordered, removed, or added before confirm.
// All state is local; nothing persists until ENG-1823's confirm writes the
// reviewed plan into real milestones and tasks.

export type ReviewTask = {
  key: string;
  title: string;
  description: string;
  sourcePassage: string | null;
  owner: string;
  dueDate: string;
};

export type ReviewMilestone = {
  key: string;
  title: string;
  description: string;
  targetTimeframe: string;
  sourcePassage: string | null;
  // A collaborator id (ENG-1981) — the same owner model the confirmed
  // milestones UI writes, not the tasks' free-text owner.
  ownerId: number | null;
  dueDate: string;
  tasks: ReviewTask[];
};

export type ReviewRequiredDocument = {
  key: string;
  name: string;
  description: string;
  sourcePassage: string | null;
};

export type ReviewRequirement = {
  key: string;
  description: string;
  category: string;
  sourcePassage: string | null;
};

export type ReviewPlan = {
  proposedTitle: string | null;
  summary: string;
  milestones: ReviewMilestone[];
  requiredDocuments: ReviewRequiredDocument[];
  requirements: ReviewRequirement[];
};

let nextKey = 0;
const makeKey = () => `review-${nextKey++}`;

export function reviewPlanFromProposal(proposal: PlanProposal): ReviewPlan {
  return {
    proposedTitle: proposal.proposed_title ?? null,
    summary: proposal.summary ?? "",
    milestones: (proposal.milestones ?? []).map((milestone) => ({
      key: makeKey(),
      title: milestone.title,
      description: milestone.description ?? "",
      targetTimeframe: milestone.target_timeframe ?? "",
      sourcePassage: milestone.source_passage ?? null,
      ownerId: null,
      dueDate: "",
      tasks: (milestone.tasks ?? []).map((task) => ({
        key: makeKey(),
        title: task.title,
        description: task.description ?? "",
        sourcePassage: task.source_passage ?? null,
        owner: "",
        dueDate: "",
      })),
    })),
    requiredDocuments: (proposal.required_documents ?? []).map((doc) => ({
      key: makeKey(),
      name: doc.name,
      description: doc.description ?? "",
      sourcePassage: doc.source_passage ?? null,
    })),
    requirements: (proposal.requirements ?? []).map((requirement) => ({
      key: makeKey(),
      description: requirement.description,
      category: requirement.category ?? "",
      sourcePassage: requirement.source_passage ?? null,
    })),
  };
}

// The reviewed plan as the confirm endpoint wants it (ENG-1950). What the
// review holds is what persists; a few fields deliberately stay behind:
// targetTimeframe is drafting context with no record column, and the tasks'
// free-text owner inputs carry no member identity to become assignee_id (a
// task member picker is future work — milestones got theirs in ENG-1981).
// Blank rows are sent as-is so the backend's validation errors name what is
// missing instead of rows silently vanishing at confirm.
export function confirmationPayloadFromReviewPlan(plan: ReviewPlan): PlanConfirmationPayload {
  return {
    proposed_title: plan.proposedTitle,
    milestones: plan.milestones.map((milestone) => ({
      title: milestone.title,
      description: milestone.description,
      due_at: milestone.dueDate || null,
      owner_id: milestone.ownerId,
      source_passage: milestone.sourcePassage,
      tasks: milestone.tasks.map((task) => ({
        title: task.title,
        description: task.description,
        due_at: task.dueDate || null,
        source_passage: task.sourcePassage,
      })),
    })),
    required_documents: plan.requiredDocuments.map((doc) => ({
      name: doc.name,
      description: doc.description,
      source_passage: doc.sourcePassage,
    })),
    requirements: plan.requirements.map((requirement) => ({
      description: requirement.description,
      category: requirement.category || null,
      source_passage: requirement.sourcePassage,
    })),
  };
}

function moveItem<T>(items: T[], index: number, delta: -1 | 1): T[] {
  const target = index + delta;
  if (target < 0 || target >= items.length) return items;
  const next = [...items];
  [next[index], next[target]] = [next[target], next[index]];
  return next;
}

type GrantPlanReviewEditorProps = {
  plan: ReviewPlan;
  onPlanChange: (plan: ReviewPlan) => void;
  collaborators: MunicipalityCollaborator[];
  collaboratorsLoading: boolean;
};

export function GrantPlanReviewEditor({
  plan,
  onPlanChange,
  collaborators,
  collaboratorsLoading,
}: GrantPlanReviewEditorProps) {
  const patch = (partial: Partial<ReviewPlan>) => onPlanChange({ ...plan, ...partial });

  const patchMilestone = (key: string, partial: Partial<ReviewMilestone>) =>
    patch({
      milestones: plan.milestones.map((m) => (m.key === key ? { ...m, ...partial } : m)),
    });

  return (
    <div className="space-y-5">
      {plan.proposedTitle !== null ? (
        <section className="space-y-1.5">
          <Label htmlFor="plan-proposed-title">Proposed title</Label>
          <Input
            id="plan-proposed-title"
            value={plan.proposedTitle}
            onChange={(event) => patch({ proposedTitle: event.target.value })}
          />
          <p className="text-xs text-muted-foreground">
            Suggested from the package because this opportunity was started without a title.
          </p>
        </section>
      ) : null}

      <section className="space-y-1.5">
        <Label htmlFor="plan-summary">Strategy summary</Label>
        <Textarea
          id="plan-summary"
          value={plan.summary}
          rows={3}
          onChange={(event) => patch({ summary: event.target.value })}
        />
      </section>

      <SectionHeader
        title="Milestones"
        onAdd={() =>
          patch({
            milestones: [
              ...plan.milestones,
              {
                key: makeKey(),
                title: "",
                description: "",
                targetTimeframe: "",
                sourcePassage: null,
                ownerId: null,
                dueDate: "",
                tasks: [],
              },
            ],
          })
        }
        addLabel="Add milestone"
      />
      {plan.milestones.length === 0 ? (
        <EmptyHint>No milestones yet. Add the phases of work this pursuit needs.</EmptyHint>
      ) : (
        <ul className="space-y-3" aria-label="Milestones">
          {plan.milestones.map((milestone, index) => (
            <li key={milestone.key} className="space-y-2 rounded-md border p-3">
              <div className="flex items-start gap-2">
                <div className="min-w-0 flex-1 space-y-2">
                  <Input
                    aria-label="Milestone title"
                    placeholder="Milestone title"
                    value={milestone.title}
                    onChange={(event) => patchMilestone(milestone.key, { title: event.target.value })}
                  />
                  <Textarea
                    aria-label="Milestone description"
                    placeholder="What this phase covers"
                    rows={2}
                    value={milestone.description}
                    onChange={(event) =>
                      patchMilestone(milestone.key, { description: event.target.value })
                    }
                  />
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                    <Input
                      aria-label="Milestone timeframe"
                      placeholder="Timeframe (e.g. 3 weeks before deadline)"
                      value={milestone.targetTimeframe}
                      onChange={(event) =>
                        patchMilestone(milestone.key, { targetTimeframe: event.target.value })
                      }
                    />
                    <MilestoneOwnerSelect
                      ariaLabel="Milestone owner"
                      value={milestone.ownerId}
                      onChange={(ownerId) => patchMilestone(milestone.key, { ownerId })}
                      // The fallback covers a refetch dropping the chosen
                      // collaborator mid-review: without it the trigger goes
                      // blank while the payload still carries the id.
                      collaborators={ownerSelectOptions(milestone.ownerId, null, collaborators)}
                      loading={collaboratorsLoading}
                    />
                    <Input
                      aria-label="Milestone due date"
                      type="date"
                      value={milestone.dueDate}
                      onChange={(event) =>
                        patchMilestone(milestone.key, { dueDate: event.target.value })
                      }
                    />
                  </div>
                  <SourcePassage text={milestone.sourcePassage} />
                </div>
                <RowControls
                  unit="milestone"
                  canMoveUp={index > 0}
                  canMoveDown={index < plan.milestones.length - 1}
                  onMove={(delta) => patch({ milestones: moveItem(plan.milestones, index, delta) })}
                  onRemove={() =>
                    patch({ milestones: plan.milestones.filter((m) => m.key !== milestone.key) })
                  }
                />
              </div>

              <div className="space-y-2 border-l pl-3">
                {milestone.tasks.map((task, taskIndex) => (
                  <div key={task.key} className="flex items-start gap-2">
                    <div className="min-w-0 flex-1 space-y-2">
                      <Input
                        aria-label="Task title"
                        placeholder="Task title"
                        value={task.title}
                        onChange={(event) =>
                          patchMilestone(milestone.key, {
                            tasks: milestone.tasks.map((t) =>
                              t.key === task.key ? { ...t, title: event.target.value } : t,
                            ),
                          })
                        }
                      />
                      {/* Persisted at confirm, so it must be reviewable —
                          agent-drafted text the reviewer cannot see is not
                          reviewed (ENG-1950). */}
                      <Textarea
                        aria-label="Task description"
                        placeholder="What doing this involves"
                        rows={2}
                        value={task.description}
                        onChange={(event) =>
                          patchMilestone(milestone.key, {
                            tasks: milestone.tasks.map((t) =>
                              t.key === task.key ? { ...t, description: event.target.value } : t,
                            ),
                          })
                        }
                      />
                      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                        <Input
                          aria-label="Task owner"
                          placeholder="Owner"
                          value={task.owner}
                          onChange={(event) =>
                            patchMilestone(milestone.key, {
                              tasks: milestone.tasks.map((t) =>
                                t.key === task.key ? { ...t, owner: event.target.value } : t,
                              ),
                            })
                          }
                        />
                        <Input
                          aria-label="Task due date"
                          type="date"
                          value={task.dueDate}
                          onChange={(event) =>
                            patchMilestone(milestone.key, {
                              tasks: milestone.tasks.map((t) =>
                                t.key === task.key ? { ...t, dueDate: event.target.value } : t,
                              ),
                            })
                          }
                        />
                      </div>
                      <SourcePassage text={task.sourcePassage} />
                    </div>
                    <RowControls
                      unit="task"
                      canMoveUp={taskIndex > 0}
                      canMoveDown={taskIndex < milestone.tasks.length - 1}
                      onMove={(delta) =>
                        patchMilestone(milestone.key, {
                          tasks: moveItem(milestone.tasks, taskIndex, delta),
                        })
                      }
                      onRemove={() =>
                        patchMilestone(milestone.key, {
                          tasks: milestone.tasks.filter((t) => t.key !== task.key),
                        })
                      }
                    />
                  </div>
                ))}
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 px-2"
                  onClick={() =>
                    patchMilestone(milestone.key, {
                      tasks: [
                        ...milestone.tasks,
                        {
                          key: makeKey(),
                          title: "",
                          description: "",
                          sourcePassage: null,
                          owner: "",
                          dueDate: "",
                        },
                      ],
                    })
                  }
                >
                  <Plus className="mr-1 h-3 w-3" aria-hidden />
                  Add task
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}

      <SectionHeader
        title="Required documents"
        onAdd={() =>
          patch({
            requiredDocuments: [
              ...plan.requiredDocuments,
              { key: makeKey(), name: "", description: "", sourcePassage: null },
            ],
          })
        }
        addLabel="Add required document"
      />
      {plan.requiredDocuments.length === 0 ? (
        <EmptyHint>No required documents listed.</EmptyHint>
      ) : (
        <ul className="space-y-2" aria-label="Required documents">
          {plan.requiredDocuments.map((doc, index) => (
            <li key={doc.key} className="flex items-start gap-2 rounded-md border p-3">
              <div className="min-w-0 flex-1 space-y-2">
                <Input
                  aria-label="Required document name"
                  placeholder="Document name"
                  value={doc.name}
                  onChange={(event) =>
                    patch({
                      requiredDocuments: plan.requiredDocuments.map((d) =>
                        d.key === doc.key ? { ...d, name: event.target.value } : d,
                      ),
                    })
                  }
                />
                {/* Persisted at confirm, so it must be reviewable. */}
                <Textarea
                  aria-label="Required document description"
                  placeholder="What this document must contain"
                  rows={2}
                  value={doc.description}
                  onChange={(event) =>
                    patch({
                      requiredDocuments: plan.requiredDocuments.map((d) =>
                        d.key === doc.key ? { ...d, description: event.target.value } : d,
                      ),
                    })
                  }
                />
                <SourcePassage text={doc.sourcePassage} />
              </div>
              <RowControls
                unit="required document"
                canMoveUp={index > 0}
                canMoveDown={index < plan.requiredDocuments.length - 1}
                onMove={(delta) =>
                  patch({ requiredDocuments: moveItem(plan.requiredDocuments, index, delta) })
                }
                onRemove={() =>
                  patch({
                    requiredDocuments: plan.requiredDocuments.filter((d) => d.key !== doc.key),
                  })
                }
              />
            </li>
          ))}
        </ul>
      )}

      <SectionHeader
        title="Requirements"
        onAdd={() =>
          patch({
            requirements: [
              ...plan.requirements,
              { key: makeKey(), description: "", category: "", sourcePassage: null },
            ],
          })
        }
        addLabel="Add requirement"
      />
      {plan.requirements.length === 0 ? (
        <EmptyHint>No hard requirements listed.</EmptyHint>
      ) : (
        <ul className="space-y-2" aria-label="Requirements">
          {plan.requirements.map((requirement, index) => (
            <li key={requirement.key} className="flex items-start gap-2 rounded-md border p-3">
              <div className="min-w-0 flex-1 space-y-2">
                <Textarea
                  aria-label="Requirement description"
                  placeholder="What the package obligates the applicant to do"
                  rows={2}
                  value={requirement.description}
                  onChange={(event) =>
                    patch({
                      requirements: plan.requirements.map((r) =>
                        r.key === requirement.key ? { ...r, description: event.target.value } : r,
                      ),
                    })
                  }
                />
                <Input
                  aria-label="Requirement category"
                  placeholder="Category (eligibility, match, format…)"
                  value={requirement.category}
                  onChange={(event) =>
                    patch({
                      requirements: plan.requirements.map((r) =>
                        r.key === requirement.key ? { ...r, category: event.target.value } : r,
                      ),
                    })
                  }
                />
                <SourcePassage text={requirement.sourcePassage} />
              </div>
              <RowControls
                unit="requirement"
                canMoveUp={index > 0}
                canMoveDown={index < plan.requirements.length - 1}
                onMove={(delta) => patch({ requirements: moveItem(plan.requirements, index, delta) })}
                onRemove={() =>
                  patch({
                    requirements: plan.requirements.filter((r) => r.key !== requirement.key),
                  })
                }
              />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function SectionHeader({
  title,
  onAdd,
  addLabel,
}: {
  title: string;
  onAdd: () => void;
  addLabel: string;
}) {
  return (
    <div className="flex items-center justify-between">
      <h3 className="text-sm font-semibold">{title}</h3>
      <Button variant="ghost" size="sm" className="h-7 px-2" onClick={onAdd}>
        <Plus className="mr-1 h-3 w-3" aria-hidden />
        {addLabel}
      </Button>
    </div>
  );
}

function RowControls({
  unit,
  canMoveUp,
  canMoveDown,
  onMove,
  onRemove,
}: {
  unit: string;
  canMoveUp: boolean;
  canMoveDown: boolean;
  onMove: (delta: -1 | 1) => void;
  onRemove: () => void;
}) {
  return (
    <div className="flex shrink-0 flex-col gap-1">
      <Button
        variant="ghost"
        size="sm"
        className="h-6 w-6 p-0"
        aria-label={`Move ${unit} up`}
        disabled={!canMoveUp}
        onClick={() => onMove(-1)}
      >
        <ArrowUp className="h-3.5 w-3.5" aria-hidden />
      </Button>
      <Button
        variant="ghost"
        size="sm"
        className="h-6 w-6 p-0"
        aria-label={`Move ${unit} down`}
        disabled={!canMoveDown}
        onClick={() => onMove(1)}
      >
        <ArrowDown className="h-3.5 w-3.5" aria-hidden />
      </Button>
      <Button
        variant="ghost"
        size="sm"
        className="h-6 w-6 p-0 text-destructive"
        aria-label={`Remove ${unit}`}
        onClick={onRemove}
      >
        <Trash2 className="h-3.5 w-3.5" aria-hidden />
      </Button>
    </div>
  );
}

// Where the drafting agent grounded an item, the supporting package excerpt
// rides along read-only: the reviewer's evidence, not an editable field.
function SourcePassage({ text }: { text: string | null }) {
  if (!text) return null;
  return (
    <p className="line-clamp-2 border-l-2 pl-2 text-xs italic text-muted-foreground">{text}</p>
  );
}

function EmptyHint({ children }: { children: React.ReactNode }) {
  return (
    <p className="rounded-md border border-dashed px-3 py-4 text-center text-xs text-muted-foreground">
      {children}
    </p>
  );
}

// Read-only context sections (scoring, post-award): shown when the package
// supplied them, never editable: they inform the review, they are not work
// items the confirm step will create.
export function PlanContextSections({ proposal }: { proposal: PlanProposal }) {
  const criteria = proposal.scoring?.criteria ?? [];
  const postAward = proposal.post_award?.items ?? [];
  if (criteria.length === 0 && postAward.length === 0) return null;
  return (
    <div className="space-y-3">
      {criteria.length > 0 ? (
        <section>
          <h3 className="text-sm font-semibold">Scoring criteria</h3>
          <ul className="mt-1 space-y-1 text-xs text-muted-foreground">
            {criteria.map((criterion, index) => (
              <li key={index}>
                <span className="font-medium text-foreground">{criterion.name}</span>
                {criterion.weight ? ` (${criterion.weight})` : ""}
                {criterion.description ? `: ${criterion.description}` : ""}
              </li>
            ))}
          </ul>
        </section>
      ) : null}
      {postAward.length > 0 ? (
        <section>
          <h3 className="text-sm font-semibold">Post-award obligations</h3>
          <ul className="mt-1 space-y-1 text-xs text-muted-foreground">
            {postAward.map((item, index) => (
              <li key={index}>
                <span className="font-medium text-foreground">{item.title}</span>
                {item.description ? `: ${item.description}` : ""}
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  );
}
